/**
 * UAT Test Scripts Service
 * Lists available editions/versions from the test scripts bucket.
 * Loads test procedures from API (DB or GCS extraction) and updates scenario flags.
 */

import axios from 'axios';
import { apiClient } from './api';

export const getUatTestScriptVersions = async () => {
  try {
    const response = await apiClient.get('/uat/test-script-versions');
    return response.data;
  } catch (error) {
    console.error('Error fetching UAT test script versions:', error);
    throw error;
  }
};

export default {
  getUatTestScriptVersions
};

const isLocalDevelopment = window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.startsWith('192.168.') ||
  window.location.hostname.startsWith('10.') ||
  window.location.hostname === '[::1]';

const API_BASE_URL = isLocalDevelopment
  ? `${window.location.protocol}//${window.location.hostname}:5001/api`
  : `${window.location.protocol}//${window.location.hostname}/api`;

const uatErrorFields = (body = {}) => ({
  error_code: body.error_code,
  error_detail: body.error_detail,
  scope_item_code: body.scope_item_code,
  edition: body.edition,
  version: body.version,
  project_scope: body.project_scope,
  bucket: body.bucket,
  gcs_prefix: body.gcs_prefix,
  missing_edition: body.missing_edition,
  missing_version: body.missing_version,
  invalid_edition: body.invalid_edition,
  invalid_version: body.invalid_version,
});

/**
 * Fetch test procedure rows for a scope item (loads from DB or extracts from GCS docx once).
 * @returns {Promise<{ success: boolean, rows?: array, source?: string, error?: string }>}
 */
export const fetchTestProcedures = async (projectId, scopeItemId) => {
  try {
    const { data } = await axios.get(
      `${API_BASE_URL}/uat/project/${projectId}/scope-items/${scopeItemId}/test-procedures`,
      { withCredentials: true, timeout: 120000 }
    );
    return data;
  } catch (error) {
    const body = error.response?.data || {};
    const msg = body.error || error.message || 'Failed to load test procedures';
    return {
      success: false,
      error: msg,
      ...uatErrorFields(body),
    };
  }
};

/**
 * Persist scenario_in_scope for one row.
 */
export const patchScenarioInScope = async (rowId, projectId, scenarioInScope) => {
  try {
    const { data } = await axios.patch(
      `${API_BASE_URL}/uat/test-procedure-rows/${rowId}`,
      { project_id: projectId, scenario_in_scope: scenarioInScope },
      { withCredentials: true }
    );
    return data;
  } catch (error) {
    const msg = error.response?.data?.error || error.message || 'Failed to update';
    return { success: false, error: msg };
  }
};

/**
 * Parse a modified scope Excel workbook for a scope item. This validates and returns
 * desired Scenario in scope selections; it does not save changes.
 */
export const uploadUatScopeExcel = async (projectId, scopeItemId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const { data } = await axios.post(
      `${API_BASE_URL}/uat/project/${projectId}/scope-items/${scopeItemId}/scope-excel-import`,
      formData,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      }
    );
    return data;
  } catch (error) {
    const body = error.response?.data || {};
    return {
      success: false,
      error: body.error || error.message || 'Failed to upload scope Excel',
      invalid_values: body.invalid_values || [],
      unmatched_steps: body.unmatched_steps || [],
      missing_steps: body.missing_steps || [],
      ...uatErrorFields(body),
    };
  }
};

/** First token of scope item label, e.g. J58 from "J58 Accounts Payable". */
const scopeItemCodeForFilename = (scopeItem) => {
  const s = (scopeItem || '').trim();
  const m = /^([A-Za-z0-9]+)/.exec(s);
  return m ? m[1] : 'scope_item';
};

/** Match backend _safe_project_name_for_filename for download fallback names. */
const projectNameForFile = (name) => {
  let s = (name || '').trim();
  if (!s) return 'Project';
  s = s.replace(/[<>:"/\\|?*\u0000-\u001f]+/g, '_').replace(/\s+/g, '_');
  s = s.replace(/^[._]+|[._]+$/g, '');
  if (!s) return 'Project';
  return s.length > 120 ? s.slice(0, 120) : s;
};

/**
 * Download filled Excel (Step, Test procedures, Scenario in scope).
 * Filename: UAT_Test_Script_Template_<scope item code>.xlsx
 * @param {string} [scopeItemLabel] — e.g. workshop scope_item text; used for filename if Content-Disposition is missing
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const downloadUatTestScriptExcel = async (projectId, scopeItemId, scopeItemLabel) => {
  const url = `${API_BASE_URL}/uat/project/${projectId}/scope-items/${scopeItemId}/test-script-export`;
  const fallbackName = `UAT_Test_Script_Template_${scopeItemCodeForFilename(scopeItemLabel)}.xlsx`;
  try {
    const response = await fetch(url, { credentials: 'include' });
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok) {
      let err = 'Failed to download test script';
      if (contentType.includes('application/json')) {
        try {
          const d = await response.json();
          err = d.error || err;
          return {
            ok: false,
            error: err,
            ...uatErrorFields(d),
          };
        } catch (_) {
          /* ignore */
        }
      }
      return { ok: false, error: err };
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    let filename = fallbackName;
    const mStar = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
    const mQuoted = /filename="([^"]+)"/i.exec(disposition);
    const mPlain = /filename=([^;\n]+)/i.exec(disposition);
    if (mStar) {
      filename = decodeURIComponent(mStar[1].trim());
    } else if (mQuoted) {
      filename = mQuoted[1];
    } else if (mPlain) {
      filename = mPlain[1].replace(/['"]/g, '').trim();
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error.message || 'Failed to download test script' };
  }
};

/**
 * Download filtered test script docx (out-of-scope procedure sections removed).
 * Filename: <project name>_Test_Script_<scope item code>.docx
 * @param {string} [projectName] — project name for filename fallback
 * @returns {Promise<{ ok: boolean, error?: string, missing_edition?: boolean, missing_version?: boolean }>}
 */
export const downloadUatTestScriptDocx = async (projectId, scopeItemId, scopeItemLabel, projectName) => {
  const url = `${API_BASE_URL}/uat/project/${projectId}/scope-items/${scopeItemId}/test-script-docx-export`;
  const pn = projectNameForFile(projectName);
  const fallbackName = `${pn}_Test_Script_${scopeItemCodeForFilename(scopeItemLabel)}.docx`;
  try {
    const response = await fetch(url, { credentials: 'include' });
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok) {
      let err = 'Failed to download test script document';
      if (contentType.includes('application/json')) {
        try {
          const d = await response.json();
          err = d.error || err;
          return {
            ok: false,
            error: err,
            ...uatErrorFields(d),
          };
        } catch (_) {
          /* ignore */
        }
      }
      return { ok: false, error: err };
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    let filename = fallbackName;
    const mStar = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
    const mQuoted = /filename="([^"]+)"/i.exec(disposition);
    const mPlain = /filename=([^;\n]+)/i.exec(disposition);
    if (mStar) {
      filename = decodeURIComponent(mStar[1].trim());
    } else if (mQuoted) {
      filename = mQuoted[1];
    } else if (mPlain) {
      filename = mPlain[1].replace(/['"]/g, '').trim();
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error.message || 'Failed to download test script document' };
  }
};

/**
 * Download original test script docx from bucket (no filtering).
 * Filename: Original_Test_Script_<scope item code>.docx
 */
export const downloadUatOriginalTestScriptDocx = async (projectId, scopeItemId, scopeItemLabel) => {
  const url = `${API_BASE_URL}/uat/project/${projectId}/scope-items/${scopeItemId}/test-script-docx-original`;
  const fallbackName = `Original_Test_Script_${scopeItemCodeForFilename(scopeItemLabel)}.docx`;
  try {
    const response = await fetch(url, { credentials: 'include' });
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok) {
      let err = 'Failed to download original test script document';
      if (contentType.includes('application/json')) {
        try {
          const d = await response.json();
          err = d.error || err;
          return {
            ok: false,
            error: err,
            ...uatErrorFields(d),
          };
        } catch (_) {
          /* ignore */
        }
      }
      return { ok: false, error: err };
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    let filename = fallbackName;
    const mStar = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
    const mQuoted = /filename="([^"]+)"/i.exec(disposition);
    const mPlain = /filename=([^;\n]+)/i.exec(disposition);
    if (mStar) {
      filename = decodeURIComponent(mStar[1].trim());
    } else if (mQuoted) {
      filename = mQuoted[1];
    } else if (mPlain) {
      filename = mPlain[1].replace(/['"]/g, '').trim();
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error.message || 'Failed to download original test script document' };
  }
};

export const fetchUatGlobalVariables = async (projectId) => {
  try {
    const { data } = await axios.get(
      `${API_BASE_URL}/uat/project/${projectId}/global-variables`,
      { withCredentials: true }
    );
    return data;
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to load global variables',
    };
  }
};

export const downloadUatGlobalVariablesExcel = async (projectId, projectName) => {
  const url = `${API_BASE_URL}/uat/project/${projectId}/global-variables-template`;
  const fallbackName = `Global_Variables_${projectNameForFile(projectName)}.xlsx`;
  try {
    const response = await fetch(url, { credentials: 'include' });
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok) {
      let err = 'Failed to download global variables Excel';
      if (contentType.includes('application/json')) {
        try {
          const d = await response.json();
          err = d.error || err;
        } catch (_) {
          /* ignore */
        }
      }
      return { ok: false, error: err };
    }

    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    let filename = fallbackName;
    const mStar = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
    const mQuoted = /filename="([^"]+)"/i.exec(disposition);
    const mPlain = /filename=([^;\n]+)/i.exec(disposition);
    if (mStar) {
      filename = decodeURIComponent(mStar[1].trim());
    } else if (mQuoted) {
      filename = mQuoted[1];
    } else if (mPlain) {
      filename = mPlain[1].replace(/['"]/g, '').trim();
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error.message || 'Failed to download global variables Excel' };
  }
};

export const uploadUatGlobalVariablesExcel = async (projectId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axios.post(
      `${API_BASE_URL}/uat/project/${projectId}/global-variables`,
      formData,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return data;
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to upload global variables Excel',
    };
  }
};
